import React from "react";
import { Grid, Box, Typography, Chip, Button } from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Download as DownloadIcon,
  Description as WordIcon,
  TableChart as ExcelIcon,
  Email as MsgIcon
} from "@mui/icons-material";
import { colors, fonts, spacing, borderRadius } from "../../../constants";
import {
  getStatusColor,
  getPriorityColor,
  formatStatusValue
} from "../../../utils/queryDisplayUtils";
import { formatQueryProjectNameForDisplay } from "../../../utils/projectsUtils";
import {
  getFileUrl,
  getFileName,
  formatFileSize,
  isImageFile,
  isPdfFile,
  isWordFile,
  isExcelFile,
  isMsgFile
} from "../../../utils/fileUtils";
import SectionCard from "./SectionCard";
import FieldLabel from "./FieldLabel";
import FieldValue from "./FieldValue";

/**
 * QueryInfoSection Component
 * Displays query information (header, type, project, region, description, attachments)
 *
 * @param {Object} query - Query object
 * @param {function} onAttachmentClick - Handler when an attachment is clicked for preview
 */
const QueryInfoSection = ({
  query,
  projectsCatalog = [],
  onAttachmentClick
}) => {
  if (!query) return null;

  const projectDisplay = formatQueryProjectNameForDisplay(
    query.projectName,
    projectsCatalog
  );

  const queryAttachments = query.attachments || [];

  return (
    <Box sx={{ mt: spacing.md }}>
      <SectionCard title="Query Information">
        <Grid container spacing={2} sx={{ width: "100%", margin: 0 }}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: "flex",
                gap: spacing.sm,
                alignItems: "center",
                mb: spacing.xs
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: fonts.weightBold,
                  fontSize: fonts.fontSizeMd,
                  color: colors.textPrimaryDark,
                  flex: 1
                }}
              >
                {query.queryHeader}
              </Typography>
              <Chip
                label={formatStatusValue(
                  query.statusLabel || query.status || "N/A"
                ).toUpperCase()}
                size="small"
                sx={{
                  backgroundColor:
                    getStatusColor(query.statusLabel || query.status) + "20",
                  color: getStatusColor(query.statusLabel || query.status),
                  fontWeight: fonts.weightSemiBold,
                  fontSize: fonts.fontSizeXs
                }}
              />
              <Chip
                label={query.priority?.toUpperCase()}
                size="small"
                sx={{
                  backgroundColor: getPriorityColor(query.priority) + "20",
                  color: getPriorityColor(query.priority),
                  fontWeight: fonts.weightSemiBold,
                  fontSize: fonts.fontSizeXs
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: colors.textSecondary,
                fontSize: fonts.fontSizeSm,
                mb: spacing.sm
              }}
            >
              Query ID: {query.queryId || query._id}
            </Typography>
            {/* {query.statusResponse && (
            <Box
              sx={{
                mb: spacing.sm,
                p: spacing.xs,
                borderRadius: borderRadius.input,
                background: 'linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)',
                border: `1px solid ${colors.borderDefault}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: colors.textSecondary,
                  fontWeight: fonts.weightSemiBold,
                  mb: '4px',
                  fontSize: fonts.fontSizeXs,
                }}
              >
                Status Response
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textPrimaryDark,
                  fontSize: fonts.fontSizeSm,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {query.statusResponse}
              </Typography>
            </Box>
          )} */}
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              maxWidth: { sm: "calc(50% - 8px)" },
              flexBasis: { sm: "calc(50% - 8px)" }
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <FieldLabel label="Query Type" />
              <FieldValue
                value={
                  query.queryType
                    ?.replace("_", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase()) || "N/A"
                }
              />
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              maxWidth: { sm: "calc(50% - 8px)" },
              flexBasis: { sm: "calc(50% - 8px)" }
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <FieldLabel label="Project Name" />
              <FieldValue value={projectDisplay || "N/A"} />
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              maxWidth: { sm: "calc(50% - 8px)" },
              flexBasis: { sm: "calc(50% - 8px)" }
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <FieldLabel label="Report Section" />
              <FieldValue value={query.visualDescription || "N/A"} />
            </Box>
          </Grid>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{
              maxWidth: { sm: "calc(50% - 8px)" },
              flexBasis: { sm: "calc(50% - 8px)" }
            }}
          >
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <FieldLabel label="Region" />
              <FieldValue value={query.region || "N/A"} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <FieldLabel label="Description" />
              <Box
                sx={{
                  p: spacing.sm,
                  background:
                    "linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)",
                  borderRadius: borderRadius.input,
                  border: `1px solid ${colors.borderDefault}`,
                  minHeight: "80px"
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    color: colors.textPrimaryDark,
                    fontSize: fonts.fontSizeMd
                  }}
                >
                  {query.queryDescription || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* ── Attachments Section ── */}
          {queryAttachments.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{ display: "flex", flexDirection: "column", width: "100%" }}
              >
                <FieldLabel
                  label={`Attachments (${queryAttachments.length})`}
                />
                <Box
                  sx={{
                    p: spacing.sm,
                    background:
                      "linear-gradient(135deg, #ffffff 0%, #f6f9ff 100%)",
                    borderRadius: borderRadius.input,
                    border: `1px solid ${colors.borderDefault}`
                  }}
                >
                  {/* Thumbnail previews for images */}
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: spacing.sm }}
                  >
                    {queryAttachments.map((file, idx) => {
                      const fileUrl = getFileUrl(file);
                      const fileName = getFileName(file);
                      const fileSize = formatFileSize(
                        file.fileSize || file.size
                      );
                      const isImage = isImageFile(file);
                      const isPdf = isPdfFile(file);

                      return (
                        <Box
                          key={file._id || idx}
                          onClick={() =>
                            onAttachmentClick && onAttachmentClick(file)
                          }
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: isImage ? 140 : "auto",
                            cursor: "pointer",
                            borderRadius: borderRadius.input,
                            border: `1px solid ${colors.borderDefault}`,
                            overflow: "hidden",
                            transition: "all 0.2s ease",
                            backgroundColor: colors.backgroundWhite,
                            "&:hover": {
                              borderColor: colors.primary,
                              boxShadow: `0 4px 12px ${colors.primaryShadow || "rgba(14,23,42,0.12)"}`,
                              transform: "translateY(-2px)"
                            }
                          }}
                        >
                          {/* Preview area */}
                          {isImage && fileUrl ? (
                            <Box
                              sx={{
                                width: "100%",
                                height: 100,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f5f5f5",
                                overflow: "hidden"
                              }}
                            >
                              <img
                                src={fileUrl}
                                alt={fileName}
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "100%",
                                  objectFit: "cover"
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.innerHTML =
                                    '<span style="color:#999;font-size:12px">Preview unavailable</span>';
                                }}
                              />
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                width: isImage ? "100%" : 56,
                                height: isImage ? 100 : 56,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: isPdf
                                  ? "#fee2e2"
                                  : isWordFile(file)
                                    ? "#dbeafe"
                                    : isExcelFile(file)
                                      ? "#d1fae5"
                                      : isMsgFile(file)
                                        ? "#fef3c7"
                                        : "#e0e7ff"
                              }}
                            >
                              {isPdf ? (
                                <PdfIcon
                                  sx={{ fontSize: "2rem", color: "#dc2626" }}
                                />
                              ) : isWordFile(file) ? (
                                <WordIcon
                                  sx={{ fontSize: "2rem", color: "#2563eb" }}
                                />
                              ) : isExcelFile(file) ? (
                                <ExcelIcon
                                  sx={{ fontSize: "2rem", color: "#059669" }}
                                />
                              ) : isMsgFile(file) ? (
                                <MsgIcon
                                  sx={{ fontSize: "2rem", color: "#d97706" }}
                                />
                              ) : (
                                <AttachFileIcon
                                  sx={{
                                    fontSize: "1.5rem",
                                    color: colors.primary
                                  }}
                                />
                              )}
                            </Box>
                          )}

                          {/* File info */}
                          <Box
                            sx={{
                              width: "100%",
                              p: "6px 8px",
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px"
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: fonts.weightSemiBold,
                                fontSize: "11px",
                                color: colors.textPrimaryDark,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: isImage ? 124 : 160
                              }}
                              title={fileName}
                            >
                              {fileName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "10px",
                                color: colors.textSecondary
                              }}
                            >
                              {fileSize}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </SectionCard>
    </Box>
  );
};

export default QueryInfoSection;
